// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Every user-facing string.
 *
 * They live in one module because the honesty gate is enforceable only if the claims are in one
 * place: a test walks this file and fails on any of the forbidden phrases below. A security tool
 * that overstates what it knows is worse than no tool, and the failure is always the same shape —
 * a confident sentence about something that was never measured.
 */

export const FORBIDDEN_CLAIMS: readonly string[] = [
  'your app is safe',
  "you're secure",
  'you are secure',
  'all clear',
  'no leaks found',
  'is live',
  'still valid',
  'military-grade',
  '100% accurate',
  'guaranteed',
  'we deleted your data',
  'memory wiped',
  'zero telemetry',
  'no network requests',
  'shipped to the public',
];

export const TAGLINE = 'Which of your secrets did your build actually ship?';

export const HERO_TITLE = 'Find out which of your secrets your build actually contains.';

export const HERO_BODY =
  'Paste your .env and drop your built folder. leakmap searches for <strong>your real values</strong>, byte for byte — not a list of known key formats — and shows you every file and offset that gives them away. If you shipped [[sourcemap|sourcemaps]] too, it decodes them and hands back your original source tree.';

export const TRUST_BANNER_STRONG = 'Uploading your .env to find out whether your .env leaked is the leak.';

export const TRUST_BANNER_BODY =
  'So leakmap never uploads it. Your .env and your built files are read inside this tab and are never an argument to any network call — not to a scanner, not to an API, not to me. The Content-Security-Policy in this page proves no other destination exists; the code is what proves the two permitted ones are never handed your data. Open your network inspector and run a search: nothing you drop here appears in it.';

export const ENV_ZONE_HINT =
  'Paste it, or drop the file. leakmap asks for the .env separately because Safari filters hidden files out of both the folder picker and drag-and-drop, so a folder you pick can never contain it.';

export const BUILD_ZONE_HINT =
  'Pick <strong>dist/</strong> (or .next/, build/, out/) — not your repo root. Nothing is uploaded, and nothing is kept: closing this tab is the whole cleanup procedure.';

/** The clean result. Three qualifiers, all carried, and never a green tick. */
export const CLEAN_HEADLINE = 'No verbatim match.';

export function cleanBody(values: number, files: number, forms: number): string {
  return (
    `None of the ${values} value${values === 1 ? '' : 's'} from your .env appear as an exact byte sequence in the ` +
    `${files} file${files === 1 ? '' : 's'} you gave leakmap, in any of the ${forms} form${forms === 1 ? '' : 's'} it checks ` +
    '(the literal, and the escape forms your minifier could have produced).\n\n' +
    'That is the only thing this result means. It is not a clean bill of health, and leakmap will not give you one.'
  );
}

export const CLEAN_CAVEATS: readonly { title: string; body: string }[] = [
  {
    title: 'Transformed beyond those forms',
    body:
      'A bundler that splits a string across a "+" concatenation, a custom obfuscator, a compressed literal. Exact matching is what makes the hits trustworthy; it is also exactly what makes these misses possible.',
  },
  {
    title: 'Out of scope',
    body:
      'leakmap only read the folder you dropped. A different build, a variable injected at deploy time, a CI secret, a sourcemap served from a CDN you did not include, or a map advertised only by your host’s SourceMap response header: all invisible here.',
  },
  {
    title: 'Not in your .env',
    body:
      'leakmap’s needles are your values and nothing else. A key hardcoded in source that never went through .env cannot be found this way — your recovered source tree, if you have one, is where to look.',
  },
  {
    title: 'Not textual at all',
    body: 'An endpoint that returns the key, a token minted client-side, a permissive CORS policy. leakmap reads bytes, not behaviour.',
  },
];

export function findingsHeadline(found: number, total: number): string {
  return `${found} of your ${total} value${total === 1 ? '' : 's'} appear verbatim in the files you gave leakmap.`;
}

export const FINDINGS_BODY =
  'Anyone who can fetch these files from wherever you deploy this folder can read those values. leakmap did not test them against any API and does not know whether they still work — rotate them anyway; that decision is cheaper than the question.';

export const HOW_STEPS: readonly { title: string; body: string }[] = [
  {
    title: '1 · Your values become the needles',
    body:
      'leakmap parses your .env with <em>both</em> parsers that real bundlers use — dotenv, and node’s parseEnv, which is what Vite 8 actually runs — and takes the union, because they disagree on real input and a missing needle is a silent false clean. It then applies your .env family precedence, so a value in .env.production.local correctly beats the one in .env.',
  },
  {
    title: '2 · Expansion, because the file is not the bundle',
    body:
      'If a value contains <code>${OTHER_VAR}</code>, the string in your build is not the string in your file. leakmap runs all three expansion engines Vite and Next have shipped and enrols every distinct result.',
  },
  {
    title: '3 · A gate decides what is worth searching for',
    body:
      'Searching a bundle for <code>true</code>, <code>3000</code> or <code>en</code> returns tens of thousands of meaningless hits. Eight rules filter those out — and <strong>every skipped variable is listed by name with the reason and a "search anyway" button</strong>. Nothing is dropped silently.',
  },
  {
    title: '4 · Escape variants, because your minifier rewrote them',
    body:
      'A value containing a backslash, a tab, <code>&lt;/script&gt;</code> or a newline is not written literally into a bundle. leakmap enrols the forms each minifier would have produced, which is the difference between finding 9 of 14 planted secrets and finding all 14.',
  },
  {
    title: '5 · One pass over every text asset',
    body:
      'A single [[aho-corasick|Aho–Corasick]] automaton searches the raw bytes of every JS, CSS, HTML, JSON, SVG and .map file in a Web Worker — plus any <code>.gz</code> siblings, inflated first. Byte offsets are exact.',
  },
  {
    title: '6 · Sourcemaps, found three ways and decoded here',
    body:
      'leakmap globs for <code>.map</code> files, scans asset contents for both comment forms including the inline base64 one, <em>and</em> reports an orphan map that nothing references. It decodes the [[vlq|base64-VLQ]] mappings itself and unpacks <code>sourcesContent</code> into a recoverable source tree.',
  },
];

export const LIMITS_HONESTY =
  'leakmap tells you what is in the bytes you gave it. It never tells you that you are safe, and it never tells you whether a key still works.';

export const LIMITS: readonly string[] = [
  '<strong>"Not found" means "not found verbatim."</strong> A secret your bundler split across a concatenation, or an obfuscator rewrote, is a secret leakmap will miss. Exact matching is what makes the hits trustworthy and what makes these misses possible.',
  '<strong>leakmap only saw the files you gave it</strong> — not your deployment. A map you cannot see locally may still be advertised by your host’s <code>SourceMap</code> response header, which takes precedence over the in-file comment.',
  '<strong>A hit inside a .map file is only public if you actually deploy .map files.</strong> leakmap has no way to know whether you do.',
  '<strong>It never contacts a provider.</strong> Validating a key would mean transmitting your secret to a third party — the exact thing this tool exists to detect. So leakmap reports a shape, never a status.',
  '<strong>Index maps are refused, not guessed.</strong> If your build produced a <code>sections</code> map, leakmap recovers the source but will not give you an original line number, because a partially-correct index-map decoder points at the wrong line with total confidence.',
  '<strong>Brotli assets are not scanned.</strong> The browser exposes no brotli decompressor to us. They are listed by name and count rather than passed over in silence.',
  '<strong>A sourcemap does not tell you where a build-inlined secret came from.</strong> The literal never existed in your source, so the mapping points at the expression that was replaced. leakmap labels those findings differently and attributes them by variable name instead.',
];

export const PRIVACY_PROTECTED: readonly string[] = [
  'Your .env and your built files are read, indexed and searched inside this browser tab. There is no endpoint in this application that receives either one. Uploading a secrets file to a remote scanner to learn whether the secrets leaked <em>is</em> the disclosure — that absurdity is the entire reason leakmap is shaped this way.',
  '<strong>No value is ever tested against a live API.</strong> leakmap will never tell you whether a key still works, because finding that out means transmitting it. That is not a setting, and it never will be.',
  '<strong>Every value in every artefact is masked, and there is no reveal button.</strong> Not in the report, not in the CSV, not in the fix notes, not in the context around a match, not in the event log. You already have the .env.',
  '<strong>Nothing goes in the URL.</strong> No hash, no query string, no path state — because the analytics beacon reports the page URL, so anything in the address bar would be transmitted.',
  'Exactly one thing persists between visits, named here: <code>leakmap.theme</code>. No IndexedDB, no cookies, no session storage, no origin-private file system, no stored folder handle.',
  'No cookies, no fingerprinting, no third-party fonts, no telemetry about your files.',
];

export const PRIVACY_NOT: readonly string[] = [
  '<strong>Masking is a spill guard, not a security boundary.</strong> Every value leakmap reports as present is, by definition, already readable by anyone who can fetch your build. The mask stops it reaching your ticket tracker, your Slack, your screen share and your clipboard.',
  '<strong><code>recovered-source.zip</code> is not masked and cannot be.</strong> It is a verbatim copy of the <code>sourcesContent</code> your own sourcemaps already publish — that is the finding. It may contain secrets that are not in your .env, and its paths may carry your home directory, username and machine name. Treat it as the thing you are trying to stop shipping.',
  'Sourcemap <code>sources</code> paths routinely contain your username and absolute home directory. leakmap masks any of your own values that appear in them, but the paths themselves are shown.',
  'Copying anything to the clipboard puts it on the system clipboard, which macOS/iOS Universal Clipboard and Windows Cloud Clipboard sync between your devices.',
  'A browser extension with access to this page can read everything in it. leakmap cannot defend against that, and neither can any other page.',
  'JavaScript cannot guarantee memory is overwritten. Closing the tab is the strongest thing either of us can do, and leakmap will not pretend otherwise with a "wipe" button.',
  'GitHub Pages logs the initial page load, as any host would.',
];

export const PRIVACY_TRUST: readonly string[] = [
  'The static bundle served by GitHub Pages, and the TLS chain to it.',
  'A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your files are never sent to it. It reports the page URL, which is why leakmap never puts anything in the URL.',
  'Feedback you choose to send goes to <code>feedback.benrichardson.dev</code>. Nothing is sent unless you open the feedback form and press Send; your files and data never are. <strong>Do not paste a real secret into it.</strong>',
  'The Content-Security-Policy in this page proves that <strong>no fourth destination exists</strong>. It cannot prove what is sent to the two that do — a CSP constrains destination, never payload. That part is the code, which is open.',
  '<code>frame-ancestors</code> is ignored in a <code>&lt;meta&gt;</code> policy and GitHub Pages cannot set response headers, so leakmap makes no header-based anti-framing claim. It refuses to render inside a frame in JavaScript instead, which is weaker, and is said plainly rather than implied by a directive that does nothing.',
];

export const ABOUT =
  'leakmap is a client-side tool for the moment after you deploy, when you realise your framework inlines every <code>VITE_</code> and <code>NEXT_PUBLIC_</code> variable into the JavaScript it serves and you cannot tell which of yours went with it. It searches for your own values rather than for a list of known key formats, which is why its hits are worth acting on — and why it is careful to tell you what a miss does not mean.';

export const GLOSSARY: Readonly<Record<string, string>> = {
  sourcemap:
    'A file your bundler can emit alongside minified JavaScript so a debugger can show you the original code. It usually contains your entire original source in a field called sourcesContent — which is public if you deploy the map.',
  'aho-corasick':
    'An algorithm that searches for many strings at once in a single pass over the text, at a cost that barely changes as you add more strings. It is what lets leakmap look for every value in your .env, in every escaped form, without reading your files more than once.',
  vlq:
    'Variable-length quantity — the compact base64 encoding a sourcemap uses to store the position of every piece of generated code. Decoding it wrongly is how tools end up pointing confidently at the wrong line.',
  rls:
    'Row Level Security — Postgres policies that decide which rows a request may read or write. A Supabase anon key is only as safe as these; a service_role key skips them entirely.',
  entropy: 'A measure of how varied the characters in a string are. Real credentials are varied; "aaaaaaaa" and "production" are not.',
};

export const KEYBOARD: readonly { keys: string; what: string }[] = [
  { keys: 'Cmd/Ctrl + Enter', what: 'Run the search' },
  { keys: 'L', what: 'Open the event log' },
  { keys: 'Esc', what: 'Close whatever is open' },
  { keys: '?', what: 'This help' },
];
